import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { LocalizationService } from '../i18n/localization.service';
import { TranslatePipe } from '../i18n/translate.pipe';
import { AuthService } from './auth.service';

@Component({
  selector: 'app-change-password-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatProgressSpinnerModule, TranslatePipe],
  template: `
    <h2 mat-dialog-title>{{ 'account.changePassword.title' | t }}</h2>
    <mat-dialog-content>
      <form class="password-form" [formGroup]="form" (ngSubmit)="submit()">
        <mat-form-field>
          <mat-label>{{ 'account.changePassword.currentPassword' | t }}</mat-label>
          <input matInput type="password" autocomplete="current-password" formControlName="currentPassword" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ 'account.changePassword.newPassword' | t }}</mat-label>
          <input matInput type="password" autocomplete="new-password" formControlName="newPassword" />
        </mat-form-field>

        <mat-form-field>
          <mat-label>{{ 'account.changePassword.confirmPassword' | t }}</mat-label>
          <input matInput type="password" autocomplete="new-password" formControlName="confirmPassword" />
        </mat-form-field>

        @if (errorMessage()) {
          <div class="dialog-error">{{ errorMessage() }}</div>
        }
      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" [disabled]="loading()" (click)="close()">{{ 'account.changePassword.cancel' | t }}</button>
      <button mat-flat-button color="primary" type="button" [disabled]="form.invalid || loading()" (click)="submit()">
        @if (loading()) {
          <mat-spinner diameter="20" />
        } @else {
          <mat-icon>lock_reset</mat-icon>
        }
        {{ 'account.changePassword.save' | t }}
      </button>
    </mat-dialog-actions>
  `,
  styles: `
    .password-form {
      display: grid;
      gap: 12px;
      min-width: min(420px, calc(100vw - 64px));
      padding-top: 4px;
    }

    mat-form-field {
      width: 100%;
    }

    button[mat-flat-button] {
      display: inline-flex;
      align-items: center;
      gap: 8px;
    }

    .dialog-error {
      padding: 10px 12px;
      border: 1px solid #fecaca;
      border-radius: 8px;
      color: #b91c1c;
      background: #fef2f2;
      font-weight: 700;
      white-space: pre-wrap;
    }
  `
})
export class ChangePasswordDialogComponent {
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly auth = inject(AuthService);
  private readonly localization = inject(LocalizationService);
  private readonly dialogRef = inject(MatDialogRef<ChangePasswordDialogComponent, boolean>);

  readonly loading = signal(false);
  readonly errorMessage = signal('');
  readonly form = this.fb.group({
    currentPassword: this.fb.control('', Validators.required),
    newPassword: this.fb.control('', [Validators.required, Validators.minLength(8)]),
    confirmPassword: this.fb.control('', Validators.required)
  });

  close(): void {
    this.dialogRef.close(false);
  }

  async submit(): Promise<void> {
    if (this.form.invalid || this.loading()) {
      return;
    }

    const { currentPassword, newPassword, confirmPassword } = this.form.getRawValue();
    if (newPassword !== confirmPassword) {
      this.errorMessage.set(this.localization.translate('account.changePassword.mismatch'));
      return;
    }

    this.loading.set(true);
    this.errorMessage.set('');

    try {
      await this.auth.changePassword({ currentPassword, newPassword });
      this.dialogRef.close(true);
    } catch (error) {
      this.errorMessage.set(this.backendErrorMessage(error));
    } finally {
      this.loading.set(false);
    }
  }

  private backendErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse && error.status === 401) {
      return this.localization.translate('api.error.authenticationRequired');
    }
    if (error instanceof HttpErrorResponse && error.status === 403) {
      return this.localization.translate('api.error.accessDenied');
    }
    return this.localization.translate('account.changePassword.error');
  }
}
